
import { useEffect, useState } from "react";
import {
    deleteInvoicePOCondition,
    deleteInvoicePOCustomMapping,
    fetchInvoicePOConditions,
    saveInvoicePOCondition,
    saveInvoicePOCustomMapping,
} from "../api";
import Swal from "sweetalert2";

const CONDITION_TYPE_OPTIONS = ["Content", "Custom", "Expression", "Budget", "Amount"];

const CUSTOM_MODE_OPTIONS = [
    { value: "Range", label: "Range" },
    { value: "Contains", label: "Contains" },
    { value: "Not Contains", label: "Not Contains" },
    { value: "Starts With", label: "Starts With" },
    { value: "Ends With", label: "Ends With" },
];


const CONDITION_OPTIONS = Array.from({ length: 8 }, (_, index) => `condition${index + 1}`);


const MAPPING_BASED_MODES = new Set([]);





function getColumnHeaderOptions(source, columnHeaderOptions) {
    return columnHeaderOptions[source] || [];
}

function createEmptyCondition(index) {
    return {
        id: `condition-${Date.now()}-${index}`,
        condition: CONDITION_OPTIONS[index] || `condition${index + 1}`,
        subConditionName: "",
        conditionType: "Content",
        isMapped: false,
        customMode: "Range",
        customMapping: "",
        customValue: "",
        customRangeFrom: "",
        customRangeTo: "",
        expression: "",
        columnName: "",
        range: "",
        source: "PO",
        isEditing: false,
    };
}

function createEmptyCustomMapping() {
    return {
        id: "",
        customConditionId: "",
        rangeFrom: "",
        rangeTo: "",
        conditionValue: "",
        matchValues: "",
        approvalPath: "",
        uploadFileName: "",
    };
}

function getConditionLabel(row) {
    if (!row) {
        return "";
    }

    return `${row.condition} - ${row.subConditionName || "Unnamed custom condition"}`;
}

function conditionFromApi(condition) {
    return {
        id: condition.id,
        condition: condition.conditionKey,
        subConditionName: condition.subConditionName || "",
        conditionType: condition.conditionType || "Content",
        isMapped: Boolean(condition.isMapped),
        customMode: condition.customMode || "Range",
        customMapping: "",
        customValue: "",
        customRangeFrom: condition.customRangeFrom || "",
        customRangeTo: condition.customRangeTo || "",
        expression: condition.expression || "",
        columnName: condition.columnName || "",
        range: "",
        source: condition.source || "PO",
        displayOrder: condition.displayOrder,
        customMappings: condition.customMappings || [],
        isEditing: false,
    };
}

function mappingFromApi(mapping, condition) {
    return {
        id: mapping.id,
        customConditionId: condition.id,
        condition: condition.condition,
        subConditionName: condition.subConditionName,
        customMode: condition.customMode,
        conditionType: condition.conditionType,
        isMapped: condition.isMapped,
        rangeFrom: mapping.rangeFrom || condition.customRangeFrom || "",
        rangeTo: mapping.rangeTo || condition.customRangeTo || "",
        conditionValue: mapping.conditionValues || mapping.approvalPathReference || "",
        matchValues: Array.isArray(mapping.matchValues) ? mapping.matchValues.join(", ") : "",
        approvalPath: mapping.conditionValues || mapping.approvalPathReference || "",
        uploadFileName: mapping.sourceFileName || "",
        source: condition.source,
        columnName: condition.columnName,
        displayOrder: mapping.displayOrder,
    };
}


function ConditionRow({ row, index, columnHeaderOptions, onChange, onRemoveRow, onToggleEdit, canRemove, onOpenCustom }) {
    const isCustomRow = row.conditionType === "Custom";
    const isExpressionRow = row.conditionType === "Expression";
    const rowHeaderOptions = getColumnHeaderOptions(row.source, columnHeaderOptions);

    return (
        <tr>
            <td>
                {row.isEditing ? (
                    <select
                        value={row.condition}
                        onChange={(event) => onChange(row.id, "condition", event.target.value)}
                    >
                        {CONDITION_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                ) : (
                    <span>{row.condition || "—"}</span>
                )}
            </td>
            <td>
                {row.isEditing ? (
                    <input
                        type="text"
                        value={row.subConditionName}
                        onChange={(event) => onChange(row.id, "subConditionName", event.target.value)}
                        placeholder="Sub condition name"
                    />
                ) : (
                    <span>{row.subConditionName || "—"}</span>
                )}
            </td>
            <td>
                {row.isEditing ? (
                    <select
                        value={row.conditionType}
                        onChange={(event) => {
                            const nextType = event.target.value;
                            onChange(row.id, "conditionType", nextType);
                            if (nextType !== "Custom") {
                                onChange(row.id, "customValue", "");
                                onChange(row.id, "customMapping", "");
                                onChange(row.id, "customRangeFrom", "");
                                onChange(row.id, "customRangeTo", "");
                            }
                        }}
                    >
                        {CONDITION_TYPE_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                ) : (
                    <span>{row.conditionType}</span>
                )}
                <div className="condition-row__hint">Row {index + 1}</div>
            </td>
            <td>
                {row.isEditing ? (
                    <select
                        value={row.source}
                        onChange={(event) => {
                            onChange(row.id, "source", event.target.value);
                            onChange(row.id, "columnName", "");
                        }}
                    >
                        {Object.keys(columnHeaderOptions).map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                ) : (
                    <span>{row.source}</span>
                )}
            </td>
            <td>
                {row.isEditing ? (
                    <select value={row.columnName} onChange={(event) => onChange(row.id, "columnName", event.target.value)}>
                        <option value="">Select header</option>
                        {rowHeaderOptions.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                ) : (
                    <span>{row.columnName || "—"}</span>
                )}
            </td>
            <td>
                {row.isEditing && isCustomRow ? (
                    <div className="condition-cell-stack">
                        <select value={row.customMode} onChange={(event) => onChange(row.id, "customMode", event.target.value)}>
                            {CUSTOM_MODE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        {row.customMode === "Range" ? (
                            <div className="condition-range-inline">
                                <div className="condition-range-field">
                                    <input
                                        type="text"
                                        value={row.customRangeFrom}
                                        onChange={(event) => onChange(row.id, "customRangeFrom", event.target.value)}
                                        placeholder="Start position"
                                    />
                                </div>
                                <div className="condition-range-field">
                                    <input
                                        type="text"
                                        value={row.customRangeTo}
                                        onChange={(event) => onChange(row.id, "customRangeTo", event.target.value)}
                                        placeholder="End position"
                                    />
                                </div>
                            </div>
                        ) : null}
                    </div>
                ) : row.isEditing && isExpressionRow ? (
                    <input
                        type="text"
                        value={row.expression}
                        onChange={(event) => onChange(row.id, "expression", event.target.value)}
                        placeholder=">= 100000"
                    />
                ) : isCustomRow ? (
                    <div className="condition-cell-stack">
                        <div>
                            <strong>Mode:</strong> {row.customMode}
                        </div>
                        {row.customMode === "Range" ? (
                            <div>
                                <strong>Range:</strong> {(row.customRangeFrom || "—") + " to " + (row.customRangeTo || "—")}
                            </div>
                        ) : MAPPING_BASED_MODES.has(row.customMode) ? (
                            <div>
                                <strong>Mapping:</strong> {row.customMapping || "—"}
                            </div>
                        ) : (
                            <div>
                                <strong>Value:</strong> {row.customValue || "—"}
                            </div>
                        )}
                    </div>
                ) : isExpressionRow ? (
                    <span>{row.expression || "—"}</span>
                ) : (
                    <span style={{ color: "#999" }}>—</span>
                )}
            </td>
            <td>

                <div className="condition-row__actions">

                    <button 
                        type="button" 
                        className="condition-icon-button" 
                        onClick={() => onToggleEdit(row.id)} 
                        aria-label={row.isEditing ? "Save condition row" : "Edit condition row"}>
                        {row.isEditing ? "Save" : "Edit"}
                    </button>
                    
                    <button 
                        type="button" 
                        className="condition-icon-button condition-icon-button--danger" 
                        onClick={() => onRemoveRow(row.id)} aria-label="Remove condition row">
                        Remove
                    </button>

                </div>

            </td>
        </tr>
    );
}

function groupMappingsForDisplay(mappings) {
    const groups = new Map();

    mappings.forEach((mapping) => {
        const key = String(mapping.customConditionId);

        if (!groups.has(key)) {
            groups.set(key, {
                customConditionId: mapping.customConditionId,
                condition: mapping.condition,
                subConditionName: mapping.subConditionName,
                conditionType: mapping.conditionType,
                isMapped: mapping.isMapped,
                source: mapping.source,
                columnName: mapping.columnName,
                mappings: [],
            });
        }
        groups.get(key).mappings.push(mapping);
    });
    return Array.from(groups.values());
}


export default function ConditionsPOView() {
    const [activeTab, setActiveTab] = useState("main");
    const [draftCondition, setDraftCondition] = useState(createEmptyCondition(0));
    const [rows, setRows] = useState([]);
    const [draftMapping, setDraftMapping] = useState(createEmptyCustomMapping());
    const [customMappings, setCustomMappings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [columnHeaderOptions, setColumnHeaderOptions] = useState({
        PO: [],
        GRN: [],
    });
    const [budgetAvailable, setBudgetAvailable] = useState(false);
    const draftHeaderOptions = getColumnHeaderOptions(draftCondition.source, columnHeaderOptions);
    const mappableConditionRows = rows.filter((row) => row.conditionType !== "Expression");
    const selectedCustomCondition = mappableConditionRows.find(
        (row) => String(row.id) === String(draftMapping.customConditionId)
    );
    const isNonRangeCustomMapping =
        selectedCustomCondition?.conditionType === "Custom" &&
        selectedCustomCondition?.customMode !== "Range";
    const groupedMappings = groupMappingsForDisplay(customMappings);



    function applyApiData(payload) {
        const conditions = Array.isArray(payload)
            ? payload
            : payload?.conditions || [];

        const headers = payload?.headers || {};

        // Backend is the source of truth.
        // This now supports PO, GRN and any number of
        // AdditionalReportsInfo sources.
        setColumnHeaderOptions(headers);

        const nextRows = conditions.map(conditionFromApi);

        setRows(nextRows);

        setCustomMappings(
            nextRows.flatMap((condition) =>
                condition.customMappings.map((mapping) =>
                    mappingFromApi(mapping, condition)
                )
            )
        );
    }




    function nextAvailableCondition(currentRows) {
        const used = new Set(currentRows.map((row) => row.condition));
        const condition = CONDITION_OPTIONS.find((option) => !used.has(option));
        return condition || "";
    }

    function conditionPayload(row) {
        const displayOrder = CONDITION_OPTIONS.indexOf(row.condition) + 1;
        return {
            ...(Number.isInteger(row.id) ? { id: row.id } : {}),
            conditionKey: row.condition,
            subConditionName: row.subConditionName,
            conditionType: row.conditionType,
            isMapped: row.conditionType === "Content" ? row.isMapped : false,
            customMode: row.customMode,
            customRangeFrom: row.customRangeFrom,
            customRangeTo: row.customRangeTo,
            expression: row.expression,
            source: row.source,
            columnName: row.columnName,
            displayOrder,
        };
    }

    useEffect(() => {
        let isActive = true;

        async function loadConditions() {
            try {
                const data = await fetchInvoicePOConditions();
                
                setBudgetAvailable(Boolean(data.budget));
                console.log("Fetched PO conditions:", data);
                if (isActive) {
                    applyApiData(data);
                    //const loadedRows = data.map(conditionFromApi);
                    const conditions = Array.isArray(data)
                        ? data
                        : data?.conditions || [];
                    //const loadedRows = data.map(conditionFromApi);
                    const nextCondition = nextAvailableCondition(conditions.map(conditionFromApi));
                    setDraftCondition((current) => ({
                        ...current,
                        condition: nextCondition || "condition1",
                    }));
                }
            } catch (error) {
                if (isActive) {
                    setErrorMessage(error.message || "Unable to load PO conditions.");
                }
            } finally {
                if (isActive) {
                    setIsLoading(false);
                }
            }
        }

        loadConditions();
        return () => {
            isActive = false;
        };
    }, []);

    function updateDraft(field, value) {
        setDraftCondition((current) => ({ ...current, [field]: value }));
    }

    async function addDraftToTable() {
        setIsSaving(true);
        setErrorMessage("");
        setStatusMessage("");
        try {
            const response = await saveInvoicePOCondition(conditionPayload(draftCondition));
            applyApiData(response.data);
            const conditions = Array.isArray(response.data)
                ? response.data
                : response.data?.conditions || [];
            const nextCondition = nextAvailableCondition(conditions.map(conditionFromApi));
            setDraftCondition({
                ...createEmptyCondition(conditions.length),
                condition: nextCondition || "condition1",
                source: draftCondition.source || "PO",
            });
            setStatusMessage(response.message || "PO condition saved successfully.");
        } catch (error) {
            setErrorMessage(error.message || "Unable to save PO condition.");
        } finally {
            setIsSaving(false);
        }
    }

    function updateRow(rowId, field, value) {
        setRows((current) => current.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)));
    }

    async function toggleRowEdit(rowId) {
        const row = rows.find((item) => item.id === rowId);
        if (!row) return;
        if (!row.isEditing) {
            setRows((current) => current.map((item) => item.id === rowId ? { ...item, isEditing: true } : item));
            return;
        }

        setIsSaving(true);
        setErrorMessage("");
        setStatusMessage("");
        try {
            const response = await saveInvoicePOCondition(conditionPayload(row));
            applyApiData(response.data);
            setStatusMessage(response.message || "PO condition saved successfully.");
        } catch (error) {
            setErrorMessage(error.message || "Unable to save PO condition.");
        } finally {
            setIsSaving(false);
        }
    }

    async function removeRow(rowId) {
        if (!Number.isInteger(rowId)) {
            setRows((current) => current.filter((row) => row.id !== rowId));
            return;
        }
        setIsSaving(true);
        setErrorMessage("");
        setStatusMessage("");
        try {
            const response = await deleteInvoicePOCondition(rowId);
            applyApiData(response.data);
            setDraftMapping((current) =>
                String(current.customConditionId) === String(rowId) ? createEmptyCustomMapping() : current
            );
            setStatusMessage(response.message || "PO condition deleted successfully.");
        } catch (error) {
            setErrorMessage(error.message || "Unable to delete PO condition.");
        } finally {
            setIsSaving(false);
        }
    }

    function updateDraftMapping(field, value) {
        setDraftMapping((current) => ({ ...current, [field]: value }));
    }

    async function addDraftMappingToTable() {
        if (!selectedCustomCondition) {
            return;
        }

        const conditionValue = String(draftMapping.conditionValue || "").trim();
        const matchValues = String(draftMapping.matchValues || "").trim();

        if (!conditionValue) {
            setErrorMessage("Condition Value is required.");
            return;
        }

        const mappingRequired = 
            selectedCustomCondition.conditionType === "Custom" ||
            (
                selectedCustomCondition.conditionType === "Content" &&
                selectedCustomCondition.isMapped
            );

        if (mappingRequired && !matchValues) {
            setErrorMessage("Mapped Values are required for this condition.");
            return;
        }    

        const nextDisplayOrder =
            Math.max(
                0,
                ...(selectedCustomCondition.customMappings || []).map((mapping) => Number(mapping.displayOrder) || 0)
            ) + 1;

        setIsSaving(true);
        setErrorMessage("");
        setStatusMessage("");
        try {
            const response = await saveInvoicePOCustomMapping({
                poConditionId: selectedCustomCondition.id,
                rangeFrom: selectedCustomCondition.customMode === "Range" ? selectedCustomCondition.customRangeFrom : draftMapping.rangeFrom,
                rangeTo: selectedCustomCondition.customMode === "Range" ? selectedCustomCondition.customRangeTo : draftMapping.rangeTo,
                conditionValues: conditionValue,
                matchValues,
                approvalPathReference: conditionValue,
                sourceFileName: draftMapping.uploadFileName,
                displayOrder: nextDisplayOrder,
            });
            applyApiData(response.data);
            setDraftMapping(createEmptyCustomMapping());
            setStatusMessage(response.message || "PO custom condition mapping saved successfully.");
        } catch (error) {
            setErrorMessage(error.message || "Unable to save custom condition mapping.");
        } finally {
            setIsSaving(false);
        }
    }

    async function removeCustomMapping(mappingId) {
        setIsSaving(true);
        setErrorMessage("");
        setStatusMessage("");
        try {
            const response = await deleteInvoicePOCustomMapping(mappingId);
            applyApiData(response.data);
            setStatusMessage(response.message || "PO custom condition mapping deleted successfully.");
        } catch (error) {
            setErrorMessage(error.message || "Unable to delete custom condition mapping.");
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <section className="conditions-view">
            <div className="workspace-panel conditions-view__hero">
                <div>
                    <h2>Condition Creation Settings</h2>
                </div>
            </div>

            {errorMessage ? <div className="config-note config-note--error">{errorMessage}</div> : null}
            {statusMessage ? <div className="config-note">{statusMessage}</div> : null}

            <div className="workspace-panel checklist-tabs" role="tablist" aria-label="Condition options">
                <button
                    type="button"
                    className={`ghost-button checklist-tabs__button${activeTab === "main" ? " is-active" : ""}`}
                    onClick={() => setActiveTab("main")}
                >
                    Main Condition
                </button>
                <button
                    type="button"
                    className={`ghost-button checklist-tabs__button${activeTab === "sub" ? " is-active" : ""}`}
                    onClick={() => setActiveTab("sub")}
                >
                    Condition Mapping
                </button>
            </div>

            {activeTab === "main" ? (
                <div className="workspace-panel conditions-panel">
                    <div className="users-form__header">
                        <div>
                            <p className="eyebrow">PO Invoice Setup</p>
                            <h2>Add PO condition rule</h2>
                        </div>
                    </div>

                    <div className="config-grid conditions-grid conditions-grid--single conditions-grid--compact">
                        <label className="config-field conditions-grid__condition">
                            <span>Condition</span>
                            <select value={draftCondition.condition} onChange={(event) => updateDraft("condition", event.target.value)}>
                                {CONDITION_OPTIONS.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </label>

                        

                        <label className="config-field conditions-grid__type">
                            <span>Condition Type</span>
                            <select
                                value={draftCondition.conditionType}
                                onChange={(event) => {
                                    const nextType = event.target.value;

                                    // Budget logic is not available
                                    if (nextType === "Budget" && !budgetAvailable) {
                                        Swal.fire({
                                            icon: "warning",
                                            title: "Budget Logic Unavailable",
                                            text: "Budget logic is not uploaded.",
                                            confirmButtonText: "OK",
                                        });

                                        return;
                                    }

                                    updateDraft("conditionType", nextType);

                                    // Budget and Amount have fixed condition names
                                    if (nextType === "Budget") {
                                        updateDraft("subConditionName", "Budget");
                                    } else if (nextType === "Amount") {
                                        updateDraft("subConditionName", "Amount");
                                    }

                                    if (nextType !== "Custom") {
                                        updateDraft("customValue", "");
                                        updateDraft("customMapping", "");
                                        updateDraft("customRangeFrom", "");
                                        updateDraft("customRangeTo", "");
                                    }

                                    if (nextType !== "Content") {
                                        updateDraft("isMapped", false);
                                    }

                                    if (nextType !== "Expression") {
                                        updateDraft("expression", "");
                                    }

                                    // Budget doesn't have source/column
                                    if (nextType === "Budget") {
                                        updateDraft("source", "");
                                        updateDraft("columnName", "");
                                    }
                                }}
                            >
                                {CONDITION_TYPE_OPTIONS.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="config-field conditions-grid__sub-condition">
                            <span>Condition Name</span>
                            <input
                                type="text"
                                value={draftCondition.subConditionName}
                                onChange={(event) =>
                                    updateDraft("subConditionName", event.target.value)
                                }
                                placeholder="Enter here"
                                disabled={
                                    draftCondition.conditionType === "Budget" ||
                                    draftCondition.conditionType === "Amount"
                                }
                            />
                        </label>

                        {draftCondition.conditionType === "Content" ? (
                            <label className="config-field">
                                <span>Value Mapping</span>

                                <label className="condition-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={draftCondition.isMapped}
                                        onChange={(event) =>
                                            updateDraft("isMapped", event.target.checked)
                                        }
                                    />
                                    <span>Enable Value Mapping</span>
                                </label>

                                <div className="config-field__hint">
                                    Enable this when source values need to be mapped to business values.
                                </div>
                            </label>
                        ) : null}

                        {draftCondition.conditionType !== "Budget" ? (
                            <>
                                <label className="config-field conditions-grid__source">
                                    <span>Source</span>
                                    <select
                                        value={draftCondition.source}
                                        onChange={(event) => {
                                            updateDraft("source", event.target.value);
                                            updateDraft("columnName", "");
                                        }}
                                    >
                                        {Object.keys(columnHeaderOptions).map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                <label className="config-field conditions-grid__column-name">
                                    <span>Source Column</span>
                                    <select
                                        value={draftCondition.columnName}
                                        onChange={(event) =>
                                            updateDraft("columnName", event.target.value)
                                        }
                                    >
                                        <option value="">Select header</option>

                                        {draftHeaderOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </>
                        ) : null}

                        {draftCondition.conditionType === "Custom" ? (
                            <>
                                <label className="config-field conditions-grid__custom-mode">
                                    <span>Custom Type</span>
                                    <select
                                        value={draftCondition.customMode}
                                        onChange={(event) => {
                                            const nextMode = event.target.value;
                                            updateDraft("customMode", nextMode);
                                            if (nextMode === "Range") {
                                                updateDraft("customValue", "");
                                                updateDraft("customMapping", "");
                                            } else {
                                                updateDraft("customRangeFrom", "");
                                                updateDraft("customRangeTo", "");
                                                updateDraft("customValue", "");
                                            }
                                        }}
                                    >
                                        {CUSTOM_MODE_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                {draftCondition.customMode === "Range" ? (
                                    <label className="config-field conditions-grid__custom-value conditions-grid__custom-value--range">
                                        <span>Character Positions</span>
                                        <div className="condition-range-inline">
                                            <div className="condition-range-field">
                                                <input
                                                    type="text"
                                                    value={draftCondition.customRangeFrom}
                                                    onChange={(event) => updateDraft("customRangeFrom", event.target.value)}
                                                    placeholder="Start"
                                                />
                                            </div>
                                            <div className="condition-range-field">
                                                <input
                                                    type="text"
                                                    value={draftCondition.customRangeTo}
                                                    onChange={(event) => updateDraft("customRangeTo", event.target.value)}
                                                    placeholder="End"
                                                />
                                            </div>
                                        </div>
                                    </label>
                                ) : null}
                            </>
                        ) : draftCondition.conditionType === "Expression" ? (
                            <label className="config-field conditions-grid__custom-value">
                                <span>Expression</span>
                                <input
                                    type="text"
                                    value={draftCondition.expression}
                                    onChange={(event) => updateDraft("expression", event.target.value)}
                                    placeholder=">= 100000"
                                />
                            </label>
                        ) : null}

                        
                    </div>

                    <div className="condition-actions-bar">
                        <button className="primary-button" type="button" onClick={addDraftToTable} disabled={isSaving || isLoading || rows.length >= 8}>
                            {isSaving ? "Saving..." : "Add Condition"}
                        </button>
                    </div>

                    <div className="config-table conditions-table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Condition</th>
                                    <th>Condition Name</th>
                                    <th>Type</th>
                                    <th>Source</th>
                                    <th>Source Column / Header</th>
                                    <th>Custom Details</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: "center", color: "#999" }}>
                                            Loading PO conditions...
                                        </td>
                                    </tr>
                                ) : !rows.length ? (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: "center", color: "#999" }}>
                                            No conditions added yet.
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((row, index) => (
                                        <ConditionRow
                                            key={row.id}
                                            row={row}
                                            index={index}
                                            columnHeaderOptions={columnHeaderOptions}
                                            onChange={updateRow}
                                            onRemoveRow={removeRow}
                                            onToggleEdit={toggleRowEdit}
                                            canRemove={rows.length > 1}
                                            onOpenCustom={() => setActiveTab("sub")}
                                        />
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="workspace-panel conditions-panel">
                    <div className="users-form__header">
                        <div>
                            <p className="eyebrow">PO Invoice Setup</p>
                            <h2>Condition Mapping</h2>
                            <p className="config-view__intro">
                                Map source values to clean condition values for Approval Matrix_PO.
                            </p>
                        </div>
                    </div>

                    {!mappableConditionRows.length ? (
                        <div className="config-note">
                            Add at least one Content or Custom condition row to create condition values.
                        </div>
                    ) : null}

                    <div className={`config-grid custom-mapping-grid${isNonRangeCustomMapping ? " custom-mapping-grid--non-range" : ""}`}>
                        <label className="config-field custom-mapping-grid__wide">
                            <span>Select Condition Name</span>
                            <select
                                value={draftMapping.customConditionId}
                                onChange={(event) => updateDraftMapping("customConditionId", event.target.value)}
                            >
                                <option value="">Select condition</option>
                                {mappableConditionRows.map((row) => (
                                    <option key={row.id} value={row.id}>
                                        {getConditionLabel(row)}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="config-field">
                            <span>Condition</span>
                            <input type="text" value={selectedCustomCondition?.condition || ""} readOnly placeholder="Auto-filled" />
                        </label>

                        <label className="config-field">
                            <span>Condition Type</span>
                            <input type="text" value={selectedCustomCondition?.conditionType || ""} readOnly placeholder="Auto-filled" />
                        </label>

                        <label className="config-field">
                            <span>Source</span>
                            <input type="text" value={selectedCustomCondition?.source || ""} readOnly placeholder="Auto-filled" />
                        </label>

                        <label className="config-field">
                            <span>Source Column</span>
                            <input type="text" value={selectedCustomCondition?.columnName || ""} readOnly placeholder="Auto-filled" />
                        </label>

                        {selectedCustomCondition?.conditionType === "Custom" && selectedCustomCondition.customMode === "Range" ? (
                            <label className="config-field custom-mapping-grid__wide">
                                <span>Range</span>
                                <div className="condition-range-inline">
                                    <div className="condition-range-field">
                                        <input
                                            type="text"
                                            value={selectedCustomCondition.customRangeFrom || ""}
                                            readOnly
                                            placeholder="From value"
                                        />
                                    </div>
                                    <div className="condition-range-field">
                                        <input
                                            type="text"
                                            value={selectedCustomCondition.customRangeTo || ""}
                                            readOnly
                                            placeholder="To value"
                                        />
                                    </div>
                                </div>
                                <div className="config-field__hint">These are 1-based character positions from the source value.</div>
                            </label>
                        ) : null}

                        {selectedCustomCondition ? (
                            <label 
                                className={`config-field ${
                                    selectedCustomCondition.conditionType === "Content"
                                        ? "custom-mapping-grid__content-value"
                                        : ""
                                } ${
                                    selectedCustomCondition.conditionType === "Custom" &&
                                    selectedCustomCondition.customMode !== "Range"
                                        ? "custom-mapping-grid__mapping-row-start"
                                        : ""
                                }`}
                            >
                                <span>Condition Value</span>
                                <input
                                    type="text"
                                    value={draftMapping.conditionValue}
                                    onChange={(event) => updateDraftMapping("conditionValue", event.target.value)}
                                    placeholder="Example: HR"
                                />
                                <div className="config-field__hint">
                                    This value will appear in Approval Matrix_PO.
                                </div>
                            </label>
                        ) : null}


                        {(
                            selectedCustomCondition?.conditionType === "Custom" ||
                            (
                                selectedCustomCondition?.conditionType === "Content" &&
                                selectedCustomCondition?.isMapped
                            )
                        ) ? (
                            <label 
                                className={`config-field ${
                                    selectedCustomCondition?.conditionType === "Custom" &&
                                    selectedCustomCondition?.customMode !== "Range"
                                        ? "custom-mapping-grid__mapping-row-value"
                                        : ""
                            
                                }`}
                            >
                                
                                <span>Mapped Values</span>

                                <input
                                    type="text"
                                    value={draftMapping.matchValues}
                                    onChange={(event) =>
                                        updateDraftMapping("matchValues", event.target.value)
                                    }
                                    placeholder="Example: 101,102,103"
                                />

                                <div className="config-field__hint">
                                    Enter comma-separated values to match against the source column.
                                </div>
                            </label>

                        ) : null}


                        <div className={`custom-mapping-or${isNonRangeCustomMapping ? " custom-mapping-grid__mapping-row-or" : ""}`}>OR</div>

                        <label className={`config-field${isNonRangeCustomMapping ? " custom-mapping-grid__mapping-row-upload" : ""}`}>
                            <span>Upload Excel</span>
                            <input
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                onChange={(event) => updateDraftMapping("uploadFileName", event.target.files?.[0]?.name || "")}
                            />
                            {draftMapping.uploadFileName ? (
                                <div className="config-field__hint">{draftMapping.uploadFileName}</div>
                            ) : null}
                        </label>
                    </div>

                    <div className="condition-actions-bar">
                        <button
                            className="primary-button"
                            type="button"
                            onClick={addDraftMappingToTable}
                            disabled={!selectedCustomCondition || isSaving}
                        >
                            {isSaving ? "Saving..." : "Submit"}
                        </button>
                    </div>

                    <div className="config-table conditions-table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Condition</th>
                                    <th>Condition Name</th>
                                    <th>Source Column</th>
                                    <th>Condition Value / Mapping</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>

                                {!customMappings.length ? (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: "center", color: "#999" }}>
                                            No condition values added yet.
                                        </td>
                                    </tr>
                                ) : (
                                    groupedMappings.map((group) => (
                                        <tr key={group.customConditionId}>
                                            <td>{group.condition}</td>
                                            <td>{group.subConditionName || "-"}</td>
                                            <td>{group.columnName || "-"}</td>

                                            <td>
                                                <div className="condition-mapping-display">
                                                    {group.mappings.map((mapping) => (
                                                        <div
                                                            key={mapping.id}
                                                            className="condition-mapping-display__item"
                                                        >
                                                            {(
                                                                group.conditionType === "Custom" ||
                                                                (group.conditionType === "Content" && group.isMapped)
                                                            ) ? (
                                                                <>
                                                                    <strong>
                                                                        {mapping.conditionValue || mapping.approvalPath || "-"}
                                                                    </strong>
                                                                    {" → "}
                                                                    {mapping.matchValues || mapping.uploadFileName || "-"}
                                                                </>
                                                            ) : (
                                                                mapping.conditionValue || mapping.approvalPath || "-"
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>

                                            <td>
                                                <div className="condition-row__actions">
                                                    {group.mappings.map((mapping) => (
                                                        <button
                                                            key={mapping.id}
                                                            type="button"
                                                            className="condition-icon-button condition-icon-button--danger"
                                                            onClick={() => removeCustomMapping(mapping.id)}
                                                        >
                                                            Remove
                                                        </button>
                                                    ))}
                                                </div>
                                            </td>                                            
                                        </tr>
                                    ))
                                )}
                            </tbody>

                        </table>
                    </div>
                </div>
            )}
        </section>
    );
}


